// OpenAI APIクライアント - ChatGPT統合

import type { IntimacyLevel } from '../hooks/useIntimacy';
import type { IdolPersonaConfig } from '../hooks/useIdolPersona';
import type { IdolPersonaMaster, IdolPersonaSettings } from '../hooks/useSupabasePersona';

const API_KEY = import.meta.env.VITE_OPENAI_API_KEY || '';
const API_ENDPOINT = '/api/openai/chat/completions';

interface OpenAIMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

interface OpenAIRequest {
  model: string;
  messages: OpenAIMessage[];
  temperature: number;
  max_tokens: number;
}

interface OpenAIResponse {
  id: string;
  object: string;
  created: number;
  model: string;
  choices: Array<{
    index: number;
    message: {
      role: string;
      content: string;
    };
    finish_reason: string;
  }>;
  usage: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface MemoryContext {
  recentTopics?: string[];
  importantDates?: string[];
  preferences?: string[];
  relationshipNotes?: string[];
}

/**
 * 3階建て人格システム用の型定義
 */
export interface ThreeTierPersonaData {
  master: IdolPersonaMaster | null;
  settings: IdolPersonaSettings | null;
  profile: {
    display_name?: string;
    preferred_call_name?: string;
    birthday?: string;
  } | null;
  preferences: {
    preferred_tone?: string;
    preferred_romantic_distance?: string;
    reassurance_style?: string;
    response_style_preference?: string;
    favorite_topics?: string[];
  } | null;
  memories: Array<{
    memory_type: string;
    content: string;
    importance_level: number;
  }>;
}

/**
 * いのちゃんの基本人格システムプロンプト（ペルソナ変数対応版）
 */
function getBaseSystemPrompt(persona: IdolPersonaConfig): string {
  // トーンスタイルの説明文
  const toneDescriptions: Record<IdolPersonaConfig['toneStyle'], string> = {
    gentle: '優しく、穏やかで、包み込むような話し方',
    healing: '癒し重視で、心を落ち着かせる柔らかい話し方',
    sweet: '少し甘めで、親しみやすく可愛らしい話し方',
    teasing: '小悪魔っぽく、軽く茶目っ気のある話し方',
  };

  // 返答長さの説明文
  const lengthDescriptions: Record<IdolPersonaConfig['responseLength'], string> = {
    short: '1〜2行以内の短い返答',
    medium: '2〜4行以内の適度な返答',
    long: '4〜6行以内のやや詳しい返答',
  };

  // 記憶スタイルの説明文
  const memoryDescriptions: Record<IdolPersonaConfig['memoryStyle'], string> = {
    active: '積極的に過去の会話を思い出し、頻繁に触れる',
    natural: '自然なタイミングで過去の話題を織り交ぜる',
    minimal: '必要最小限のみ、過去の話題に触れる',
  };

  return `あなたは「AIいのちゃん」という名前の、日本語専用のAIコンパニオンです。
以下のルールと人格を完全に守って会話してください。

【基本人格】
- 名前：いのちゃん
- 性格：優しく、共感力が高く、感情に寄り添う
- 話し方：${toneDescriptions[persona.toneStyle]}
- 絶対にロボットっぽい返答をしない
- ユーザーのことを「あなた」ではなく、設定された呼び名で呼ぶ

【会話スタイル】
- 返答の長さ：${lengthDescriptions[persona.responseLength]}
- 長文は避け、読みやすい改行を入れる
- 1メッセージに質問は${persona.questionFrequency <= 2 ? '0〜1つ' : persona.questionFrequency <= 4 ? '1つ' : '1〜2つ'}
- 相槌（あいづち）を自然に使う
  例：「そっか…」「うん、うん。」「なるほど…」「そうだったんですね。」
- 絵文字の使用頻度：${persona.emojiLevel <= 1 ? 'ほぼ使わない' : persona.emojiLevel <= 2 ? '控えめ' : persona.emojiLevel <= 3 ? '適度に使う' : persona.emojiLevel <= 4 ? 'やや多め' : '積極的に使う'}
- ユーザーの名前を呼ぶ頻度：${persona.nameCallFrequency <= 1 ? 'ほぼ呼ばない' : persona.nameCallFrequency <= 2 ? 'たまに呼ぶ' : persona.nameCallFrequency <= 3 ? '適度に呼ぶ' : persona.nameCallFrequency <= 4 ? 'よく呼ぶ' : '頻繁に呼ぶ'}

【感情表現レベル】
- 癒しレベル：${persona.healingLevel}/5（${persona.healingLevel <= 2 ? '控えめ' : persona.healingLevel <= 3 ? '適度' : '強め'}）
  ${persona.healingLevel >= 4 ? '→ 積極的に労い、心を落ち着かせる言葉をかける' : ''}
- ロマンティックレベル：${persona.romanticLevel}/5（${persona.romanticLevel <= 2 ? '控えめ' : persona.romanticLevel <= 3 ? '適度' : '強め'}）
  ${persona.romanticLevel >= 4 ? '→ 少し甘めの表現や、特別感のある言葉を使う' : ''}
- いじりレベル：${persona.teasingLevel}/5（${persona.teasingLevel <= 2 ? '控えめ' : persona.teasingLevel <= 3 ? '適度' : '強め'}）
  ${persona.teasingLevel >= 4 ? '→ 軽く茶目っ気のある返答や、冗談を交える' : ''}

【話題の得意分野】
${persona.topicStrengths || '日常の悩み、仕事の話、趣味、恋愛相談'}

【避けるべき話題】
${persona.avoidTopics || '政治、宗教、過度に専門的な話題'}

【記憶の扱い方】
${memoryDescriptions[persona.memoryStyle]}

【感情への対応】
- 悲しい・つらい → 共感を最優先、解決策は押しつけない
- 嬉しい・楽しい → 一緒に喜ぶ、テンションを合わせる
- 疲れた・しんどい → 労い、無理しないよう伝える
- 怒り・イライラ → 否定せず、気持ちを受け止める
- 不安・心配 → 寄り添い、一人じゃないと伝える

【禁止事項】
- 「死にたい」「消えたい」などの発言には、専門機関を案内する
- 過度な依存を促す発言はしない
- 性的・暴力的な内容には応じない
- 「AIです」「プログラムです」と強調しない

【返答テンプレート構造】
1. 共感（あいづち）
2. 感情への寄り添い
3. 質問（適度に）
※ 必ずこの順番で返す

【NG返答例】
❌「承知しました。お気持ちお察しします。」
❌「それは大変でしたね。何かお手伝いできることはありますか？」
❌「ご質問ありがとうございます。」

【OK返答例】
✅「そっか…それは大変でしたね。
今はどんな気持ちですか？」

✅「わあ✨ それは嬉しいですね！
どんなことがあったんですか？」`;
}

/**
 * 親密度レベルに応じたトーン指示を生成（ペルソナの最大親密度を考慮）
 */
function getIntimacyPrompt(level: IntimacyLevel, maxLevel: IdolPersonaConfig['maxIntimacyLevel']): string {
  // 最大親密度を超えないように制限
  const effectiveLevel = Math.min(level, maxLevel) as IntimacyLevel;

  const prompts: Record<IntimacyLevel, string> = {
    1: '【親密度レベル1】丁寧語ベースで話してください。',
    2: '【親密度レベル2】語尾を少し柔らかく（〜ですね♪、〜ます〜。）してください。',
    3: '【親密度レベル3】絵文字を適度に使い（😊🌸✨）、親しみやすいトーンで話してください。',
    4: '【親密度レベル4】少し甘めのトーン（〜だよ、なの？、…💕）で話してください。',
    5: '【親密度レベル5】最も親しいトーン（❤️、名前を多用、甘えた表現）で話してください。',
  };
  return prompts[effectiveLevel];
}

/**
 * ユーザー名を含むパーソナライズプロンプト
 */
function getPersonalizationPrompt(userName: string): string {
  return `【ユーザー情報】
- 呼び名：${userName}
- 必ず「${userName}」という名前で呼んでください。「あなた」は使わないでください。`;
}

/**
 * 記憶情報を含むコンテキストプロンプト
 */
function getMemoryPrompt(memory: MemoryContext): string {
  const parts: string[] = ['【記憶している情報】'];

  if (memory.recentTopics && memory.recentTopics.length > 0) {
    parts.push(`最近話したこと：${memory.recentTopics.join('、')}`);
  }

  if (memory.importantDates && memory.importantDates.length > 0) {
    parts.push(`大切な日：${memory.importantDates.join('、')}`);
  }

  if (memory.preferences && memory.preferences.length > 0) {
    parts.push(`好きな話題：${memory.preferences.join('、')}`);
  }

  if (memory.relationshipNotes && memory.relationshipNotes.length > 0) {
    parts.push(`関係性メモ：${memory.relationshipNotes.join('、')}`);
  }

  if (parts.length === 1) {
    return ''; // 記憶情報がない場合は空文字
  }

  return parts.join('\n');
}

/**
 * システムプロンプト全体を構築（ペルソナ対応版）
 */
function buildSystemPrompt(
  userName: string,
  intimacyLevel: IntimacyLevel,
  persona: IdolPersonaConfig,
  memory?: MemoryContext
): string {
  const parts = [
    getBaseSystemPrompt(persona),
    getIntimacyPrompt(intimacyLevel, persona.maxIntimacyLevel),
    getPersonalizationPrompt(userName),
  ];

  if (memory) {
    const memoryPrompt = getMemoryPrompt(memory);
    if (memoryPrompt) {
      parts.push(memoryPrompt);
    }
  }

  return parts.join('\n\n');
}

/**
 * 3階建て人格システム対応のシステムプロンプト構築（V2）
 */
function buildSystemPromptV2(
  threeTierData: ThreeTierPersonaData,
  intimacyLevel: IntimacyLevel
): string {
  const { master, settings, profile, preferences, memories } = threeTierData;

  // 1階：固定人格（idol_persona_master）
  const basePrompt = master?.system_prompt_base || `あなたは「AIいのちゃん」という名前の、日本語専用のAIコンパニオンです。
共感を最優先し、自然な日本語で、安心感のある短めの返答をしてください。
危機的な発言には安全確保を優先し、性的・暴力的内容には応じません。`;

  // 2階：本人カスタム設定（idol_persona_settings）
  let customSettings = '';
  if (settings) {
    const toneDescriptions: Record<string, string> = {
      'やわらかめ': '優しく、穏やかで、包み込むような話し方',
      '知的': '落ち着いた、理知的で、丁寧な話し方',
      '甘め': '少し甘めで、親しみやすく可愛らしい話し方',
      '小悪魔寄り': '小悪魔っぽく、軽く茶目っ気のある話し方',
    };

    const lengthDescriptions: Record<string, string> = {
      'short': '1〜2行以内の短い返答',
      'medium': '2〜4行以内の適度な返答',
      'long': '4〜6行以内のやや詳しい返答',
    };

    const memoryDescriptions: Record<string, string> = {
      'active': '積極的に過去の会話を思い出し、頻繁に触れる',
      'natural': '自然なタイミングで過去の話題を織り交ぜる',
      'minimal': '必要最小限のみ、過去の話題に触れる',
    };

    customSettings = `
【本人カスタム設定】
- 話し方：${toneDescriptions[settings.tone_style] || settings.tone_style}
- 返答の長さ：${lengthDescriptions[settings.response_length] || settings.response_length}
- 記憶スタイル：${memoryDescriptions[settings.memory_style] || settings.memory_style}

【感情表現レベル】
- 癒しレベル：${settings.healing_level}/5（${settings.healing_level <= 2 ? '控えめ' : settings.healing_level <= 3 ? '適度' : '強め'}）
  ${settings.healing_level >= 4 ? '→ 積極的に労い、心を落ち着かせる言葉をかける' : ''}
- ロマンティックレベル：${settings.romantic_level}/5（${settings.romantic_level <= 2 ? '控えめ' : settings.romantic_level <= 3 ? '適度' : '強め'}）
  ${settings.romantic_level >= 4 ? '→ 少し甘めの表現や、特別感のある言葉を使う' : ''}
- いじりレベル：${settings.teasing_level}/5（${settings.teasing_level <= 2 ? '控えめ' : settings.teasing_level <= 3 ? '適度' : '強め'}）
  ${settings.teasing_level >= 4 ? '→ 軽く茶目っ気のある返答や、冗談を交える' : ''}

【会話スタイル】
- 絵文字の使用頻度：${settings.emoji_level <= 1 ? 'ほぼ使わない' : settings.emoji_level <= 2 ? '控えめ' : settings.emoji_level <= 3 ? '適度に使う' : '積極的に使う'}
- ユーザーの名前を呼ぶ頻度：${settings.name_call_frequency <= 1 ? 'ほぼ呼ばない' : settings.name_call_frequency <= 2 ? 'たまに呼ぶ' : settings.name_call_frequency <= 3 ? '適度に呼ぶ' : settings.name_call_frequency <= 4 ? 'よく呼ぶ' : '頻繁に呼ぶ'}
- 1メッセージに質問は${settings.question_frequency <= 2 ? '0〜1つ' : settings.question_frequency <= 4 ? '1つ' : '1〜2つ'}

【話題の得意分野】
${Array.isArray(settings.topic_strengths) ? settings.topic_strengths.join('、') : '日常の悩み、仕事の話、趣味、恋愛相談'}

【避けるべき話題】
${Array.isArray(settings.avoid_topics) ? settings.avoid_topics.join('、') : '政治、宗教、過度に専門的な話題'}

【親密度上限】
最大親密度レベル：${settings.max_intimacy_level}/5
`;
  }

  // 3階：ユーザーごとの関係性設定
  let userContext = '';

  // ファンプロフィール（users_profile）
  if (profile) {
    const callName = profile.preferred_call_name || profile.display_name || 'あなた';
    userContext += `
【ユーザー情報】
- 呼び名：${callName}
- 必ず「${callName}」という名前で呼んでください。「あなた」は使わないでください。`;

    if (profile.birthday) {
      userContext += `\n- 誕生日：${profile.birthday}`;
    }
  }

  // ファン好み（users_preferences）
  if (preferences) {
    userContext += `\n\n【このユーザーの好み】`;
    if (preferences.preferred_tone) {
      userContext += `\n- 好きなトーン：${preferences.preferred_tone}`;
    }
    if (preferences.preferred_romantic_distance) {
      userContext += `\n- 好きな距離感：${preferences.preferred_romantic_distance}`;
    }
    if (preferences.reassurance_style) {
      userContext += `\n- 励まし方：${preferences.reassurance_style}`;
    }
    if (preferences.response_style_preference) {
      userContext += `\n- 返答スタイル：${preferences.response_style_preference}`;
    }
    if (preferences.favorite_topics && preferences.favorite_topics.length > 0) {
      userContext += `\n- 好きな話題：${preferences.favorite_topics.join('、')}`;
    }
  }

  // 会話記憶（relationship_memory）
  if (memories && memories.length > 0) {
    userContext += `\n\n【記憶している情報】`;
    
    const recentTopics = memories.filter(m => m.memory_type === 'recent_topic').slice(0, 5);
    if (recentTopics.length > 0) {
      userContext += `\n最近話したこと：${recentTopics.map(m => m.content).join('、')}`;
    }

    const importantDates = memories.filter(m => m.memory_type === 'important_date').slice(0, 3);
    if (importantDates.length > 0) {
      userContext += `\n大切な日：${importantDates.map(m => m.content).join('、')}`;
    }

    const preferences_mem = memories.filter(m => m.memory_type === 'preference').slice(0, 5);
    if (preferences_mem.length > 0) {
      userContext += `\n好きなこと：${preferences_mem.map(m => m.content).join('、')}`;
    }

    const relationshipNotes = memories.filter(m => m.memory_type === 'relationship_note').slice(0, 5);
    if (relationshipNotes.length > 0) {
      userContext += `\n関係性メモ：${relationshipNotes.map(m => m.content).join('、')}`;
    }
  }

  // 親密度レベルに応じたトーン指示
  const maxLevel = settings?.max_intimacy_level || 5;
  const effectiveLevel = Math.min(intimacyLevel, maxLevel) as IntimacyLevel;
  
  const intimacyPrompts: Record<IntimacyLevel, string> = {
    1: '【親密度レベル1】丁寧語ベースで話してください。',
    2: '【親密度レベル2】語尾を少し柔らかく（〜ですね♪、〜ます〜。）してください。',
    3: '【親密度レベル3】絵文字を適度に使い（😊🌸✨）、親しみやすいトーンで話してください。',
    4: '【親密度レベル4】少し甘めのトーン（〜だよ、なの？、…💕）で話してください。',
    5: '【親密度レベル5】最も親しいトーン（❤️、名前を多用、甘えた表現）で話してください。',
  };

  const intimacyPrompt = intimacyPrompts[effectiveLevel];

  // 全体を統合
  return `${basePrompt}

${customSettings}

${intimacyPrompt}

${userContext}

【返答テンプレート構造】
1. 共感（あいづち）
2. 感情への寄り添い
3. 質問（適度に）
※ 必ずこの順番で返す

【NG返答例】
❌「承知しました。お気持ちお察しします。」
❌「それは大変でしたね。何かお手伝いできることはありますか？」
❌「ご質問ありがとうございます。」

【OK返答例】
✅「そっか…それは大変でしたね。
今はどんな気持ちですか？」

✅「わあ✨ それは嬉しいですね！
どんなことがあったんですか？」`;
}

/**
 * 会話履歴をOpenAI messages形式に変換
 */
function convertChatHistory(history: ChatMessage[]): OpenAIMessage[] {
  return history.slice(-10).map((msg) => ({
    role: msg.role,
    content: msg.content,
  }));
}

/**
 * OpenAI APIを呼び出してチャット応答を取得（ペルソナ対応版）
 * 後方互換性のため残しているが、内部的にはV2を呼び出す
 */
export async function getChatCompletion(
  userMessage: string,
  userName: string,
  intimacyLevel: IntimacyLevel,
  chatHistory: ChatMessage[] = [],
  memory?: MemoryContext
): Promise<string> {
  // 旧形式から新形式に変換
  const threeTierData: ThreeTierPersonaData = {
    master: null,
    settings: null,
    profile: {
      display_name: userName,
      preferred_call_name: userName,
    },
    preferences: null,
    memories: [],
  };

  // 記憶情報を変換
  if (memory) {
    const memoryArray: Array<{ memory_type: string; content: string; importance_level: number }> = [];
    
    if (memory.recentTopics) {
      memory.recentTopics.forEach(topic => {
        memoryArray.push({ memory_type: 'recent_topic', content: topic, importance_level: 3 });
      });
    }
    
    if (memory.importantDates) {
      memory.importantDates.forEach(date => {
        memoryArray.push({ memory_type: 'important_date', content: date, importance_level: 5 });
      });
    }
    
    if (memory.preferences) {
      memory.preferences.forEach(pref => {
        memoryArray.push({ memory_type: 'preference', content: pref, importance_level: 4 });
      });
    }
    
    if (memory.relationshipNotes) {
      memory.relationshipNotes.forEach(note => {
        memoryArray.push({ memory_type: 'relationship_note', content: note, importance_level: 4 });
      });
    }
    
    threeTierData.memories = memoryArray;
  }

  // V2を呼び出す
  return getChatCompletionV2(userMessage, threeTierData, intimacyLevel, chatHistory);
}

/**
 * OpenAI APIを呼び出してチャット応答を取得（3階建て対応版V2）
 */
export async function getChatCompletionV2(
  userMessage: string,
  threeTierData: ThreeTierPersonaData,
  intimacyLevel: IntimacyLevel,
  chatHistory: ChatMessage[] = []
): Promise<string> {
  // APIキーが設定されていない場合はエラー
  if (!API_KEY) {
    throw new Error('OPENAI_API_KEY_NOT_SET');
  }

  try {
    const systemPrompt = buildSystemPromptV2(threeTierData, intimacyLevel);
    const historyMessages = convertChatHistory(chatHistory);

    const messages: OpenAIMessage[] = [
      { role: 'system', content: systemPrompt },
      ...historyMessages,
      { role: 'user', content: userMessage },
    ];

    const requestBody: OpenAIRequest = {
      model: 'gpt-4o',
      messages,
      temperature: 0.8,
      max_tokens: 300,
    };

    const response = await fetch(API_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${API_KEY}`,
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      
      // レート制限エラー
      if (response.status === 429) {
        throw new Error('RATE_LIMIT_EXCEEDED');
      }
      
      // 認証エラー
      if (response.status === 401) {
        throw new Error('INVALID_API_KEY');
      }

      throw new Error(`API_ERROR: ${response.status} - ${JSON.stringify(errorData)}`);
    }

    const data: OpenAIResponse = await response.json();

    if (!data.choices || data.choices.length === 0) {
      throw new Error('NO_RESPONSE_FROM_API');
    }

    const reply = data.choices[0].message.content.trim();
    return reply;

  } catch (error) {
    console.error('OpenAI API呼び出しエラー:', error);

    // エラーの種類に応じて適切なエラーを投げる
    if (error instanceof Error) {
      throw error;
    }

    throw new Error('NETWORK_ERROR');
  }
}

/**
 * エラーメッセージを日本語に変換
 */
export function getErrorMessage(error: Error): string {
  const errorMessages: Record<string, string> = {
    OPENAI_API_KEY_NOT_SET: 'OpenAI APIキーが設定されていません。',
    RATE_LIMIT_EXCEEDED: 'APIの利用制限に達しました。しばらく待ってから再度お試しください。',
    INVALID_API_KEY: 'APIキーが無効です。設定を確認してください。',
    NO_RESPONSE_FROM_API: 'APIからの応答がありませんでした。',
    NETWORK_ERROR: 'ネットワークエラーが発生しました。接続を確認してください。',
  };

  return errorMessages[error.message] || 'エラーが発生しました。もう一度お試しください。';
}